import {
  Box,
  Chip,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Checkbox,
  Autocomplete,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import TimelineKickoffFields from "../TimelineKickoffFields";
import { getProjectName } from "../../utils/projectUtils";
import {
  FieldLabel,
  HOME_GLASS_BORDER,
  knowledgeSharingSelectSx,
  selectMenuPaperSx,
  StepSectionTitle,
  textFieldSx,
  THEME_ACCENT,
} from "./shared";
import { useAuth } from "../../../../app/context/AuthContext";
import { MODULE_LIST } from "../../../../constants/commons";

export default function ProjectDetailsStep({
  clientId,
  clientProjects,
  clientProjectsLoading,
  form,
  open,
  activeStep,
  projectTypes,
  projectTypesLoading,
  setForm,
}) {

   const { userPermissions } = useAuth();
  
    const projKnowSharingModule = Object.hasOwn(userPermissions, MODULE_LIST.PROJ_KNOW_SHARING)
      ? userPermissions[MODULE_LIST.PROJ_KNOW_SHARING]
      : false;
  const canEditProjKnowSharing = projKnowSharingModule.edit;
  
  return (
    <>
      <StepSectionTitle icon={PersonOutlineIcon}>Project Details</StepSectionTitle>
      <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.45)", mb: 2, lineHeight: 1.5 }}>
        Enter the basic information and timeline for this project.
      </Typography>
      <Stack spacing={1.5}>
        <Box>
          <FieldLabel>Project Name</FieldLabel>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Enter project name"
            value={form.projectName}
            onChange={(e) =>
              setForm((p) => ({ ...p, projectName: e.target.value }))
            }
            autoFocus={open && activeStep === 0}
            InputProps={{
              endAdornment: (
                <WorkspacesOutlinedIcon
                  sx={{ fontSize: 18, color: "rgba(255,255,255,0.3)", mr: 0.5 }}
                />
              ),
            }}
            sx={textFieldSx}
          />
        </Box>
        <Box>
          <FieldLabel>Project Type</FieldLabel>
          <Autocomplete
            freeSolo
            options={projectTypes.map((t) => t.projectType).filter(Boolean)}
            loading={projectTypesLoading}
            inputValue={form.projectTypeName}
            onInputChange={(_, value) =>
              setForm((p) => ({ ...p, projectTypeName: value }))
            }
            onChange={(_, value) =>
              setForm((p) => ({
                ...p,
                projectTypeName: String(value ?? "").trim(),
              }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                variant="outlined"
                placeholder="Select or enter project type"
                InputProps={params.InputProps}
                sx={textFieldSx}
              />
            )}
            slotProps={{
              paper: {
                sx: {
                  bgcolor: "rgba(28, 29, 22, 0.96)",
                  backdropFilter: "blur(20px)",
                  border: HOME_GLASS_BORDER,
                  color: "#fff",
                  "& .MuiAutocomplete-option": {
                    fontSize: 14,
                    "&[aria-selected='true']": {
                      bgcolor: `${THEME_ACCENT}33`,
                    },
                    "&.Mui-focused": {
                      bgcolor: "rgba(255,255,255,0.06)",
                    },
                  },
                },
              },
            }}
          />
        </Box>
        <Box sx={{
          display: canEditProjKnowSharing ? 'block' : 'none',
        }}>
          <FieldLabel>Knowledge sharing</FieldLabel>
          <Select
            fullWidth
            size="small"
            multiple
            displayEmpty
            disabled={clientProjectsLoading || !clientId}
            value={form.knowledgeSharingProjectIds}
            onChange={(event) => {
              const { value } = event.target;
              setForm((prev) => ({
                ...prev,
                knowledgeSharingProjectIds:
                  typeof value === "string" ? value.split(",") : value,
              }));
            }}
            renderValue={(selected) => {
              if (clientProjectsLoading) {
                return (
                  <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.35)" }}>
                    Loading projects...
                  </Typography>
                );
              }
              if (!selected.length) {
                return (
                  <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.35)" }}>
                    Select projects
                  </Typography>
                );
              }
              return (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((projectId) => {
                    const project = clientProjects.find(
                      (item) =>
                        String(item.projectId ?? item.Id ?? item.id) ===
                        String(projectId),
                    );
                    return (
                      <Chip
                        key={projectId}
                        label={getProjectName(project) || `Project ${projectId}`}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: 12,
                          bgcolor: "rgba(255,255,255,0.1)",
                          color: "#f8fafc",
                        }}
                      />
                    );
                  })}
                </Box>
              );
            }}
            sx={knowledgeSharingSelectSx}
            MenuProps={{ PaperProps: { sx: selectMenuPaperSx } }}
          >
            {clientProjects.map((project) => {
              const projectId = project.projectId ?? project.Id ?? project.id;
              if (projectId == null) return null;
              const projectIdStr = String(projectId);

              return (
                <MenuItem key={projectId} value={projectIdStr}>
                  <Checkbox
                    size="small"
                    checked={form.knowledgeSharingProjectIds.includes(projectIdStr)}
                    sx={{
                      p: 0.5,
                      mr: 1,
                      color: "rgba(255,255,255,0.35)",
                      "&.Mui-checked": { color: THEME_ACCENT },
                    }}
                  />
                  {getProjectName(project) || `Project ${projectId}`}
                </MenuItem>
              );
            })}
          </Select>
        </Box>
        <TimelineKickoffFields
          keyDates={form.keyDates}
          onKeyDatesChange={(keyDates) =>
            setForm((p) => ({ ...p, keyDates }))
          }
        />
      </Stack>
    </>
  );
}
